import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { AlertCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";

function formatErr(detail) {
  if (!detail) return "Login failed";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  // Structured errors, e.g. a suspended account: {code, msg}
  if (typeof detail === "object" && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// Dev-only quick sign-in accounts come from env (never hardcoded in source):
// REACT_APP_QUICK_SIGNIN_ACCOUNTS={"admin":{"email":"..","password":".."},...}
let QUICK_ACCOUNTS = {};
try {
  QUICK_ACCOUNTS = JSON.parse(process.env.REACT_APP_QUICK_SIGNIN_ACCOUNTS || "{}");
} catch {
  QUICK_ACCOUNTS = {};
}
const ROLE_META = {
  admin: { label: "Admin", note: "full access · trucks & settings" },
  dispatcher: { label: "Dispatcher", note: "jobs & assignments only" },
  truck: { label: "Truck (Driver)", note: "mobile job view + status" },
};

export default function LoginPage() {
  const { login, authError } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState(authError || "");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const u = await login(email.trim(), password);
      toast.success(`Welcome back, ${u.name}`);
      // Always route to the role's home page on a fresh login. We deliberately
      // do NOT honour `loc.state.from` — if the previous user was on /settings
      // when they logged out, the next user (a dispatcher with limited admin
      // access) shouldn't land there.
      let target = "/";
      // Feb 2026 (Commit D — de-NTS-ify): `owner` lands on the dispatcher
      // dashboard `/` — same home as admin, since owner does dispatch too.
      // Truck stays on /driver (physical role — no owner overlap).
      // Viewer still routes to /history (they're back-office / billing).
      if (u.role === "truck") target = "/driver";
      else if (u.role === "owner") target = "/";
      nav(target, { replace: true });
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const quickFill = (role) => {
    const acct = QUICK_ACCOUNTS[role];
    if (!acct) return;
    setEmail(acct.email || "");
    setPassword(acct.password || "");
  };

  const quickRoles = Object.keys(ROLE_META).filter((r) => QUICK_ACCOUNTS[r]);

  return (
    <div className="min-h-screen flex bg-slate-50" data-testid="login-page">
      {/* Left brand panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-slate-950 text-white p-12 flex-col justify-between relative overflow-hidden">
        <div>
          <div className="flex items-center gap-4">
            <img
              src="/branding/tow-hook-icon-1024.png"
              alt="Tow-Hook"
              className="h-16 w-16 rounded-2xl"
              data-testid="login-brand-logo"
            />
            <div>
              <div className="font-display font-black text-4xl tracking-tight leading-none" data-testid="login-brand-name">Tow-Hook</div>
              <div className="uppercase-label mt-1.5" style={{ color: "#64748B" }}>
                Dispatch Console
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-6 relative z-10">
          <h2 className="font-display text-4xl font-black tracking-tight leading-tight">
            Coordinate every<br/>roadside call in<br/>
            <span className="text-blue-400">one control room.</span>
          </h2>
          <p className="text-slate-400 max-w-md leading-relaxed">
            Track your fleet live, dispatch the right truck to every call, and pull jobs in
            from your partner network — all from one dispatcher dashboard.
          </p>
        </div>

        <div className="text-xs text-slate-500 relative z-10">
          <div>© {new Date().getFullYear()} Tow-Hook.com</div>
        </div>

        {/* decorative grid */}
        <div className="absolute inset-0 opacity-[0.04] pointer-events-none"
          style={{ backgroundImage: "linear-gradient(#fff 1px,transparent 1px),linear-gradient(90deg,#fff 1px,transparent 1px)", backgroundSize: "40px 40px" }} />
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <img
              src="/branding/tow-hook-icon-1024.png"
              alt="Tow-Hook"
              className="h-12 w-12 rounded-xl"
            />
            <div>
              <div className="font-display font-black text-2xl leading-none">Tow-Hook</div>
            </div>
          </div>

          <h1 className="font-display text-3xl font-black tracking-tight text-slate-900">Sign in</h1>
          <p className="text-sm text-slate-500 mt-1">Access the dispatcher console.</p>

          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="login-form">
            <div>
              <Label className="uppercase-label">Email</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                data-testid="login-email"
                required
              />
            </div>
            <div>
              <Label className="uppercase-label">Password</Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                data-testid="login-password"
                required
              />
            </div>

            {err && (
              <div className="flex items-start gap-2 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md p-2.5" data-testid="login-error">
                <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>{err}</span>
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-slate-900 hover:bg-slate-800 text-white border border-slate-800 dark:bg-transparent dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:border-slate-500 dark:hover:text-white font-semibold"
              data-testid="login-submit"
            >
              {loading ? (<><Loader2 className="h-4 w-4 animate-spin" />Signing in…</>) : "Sign In"}
            </Button>
          </form>

          <div className="mt-3 text-right">
            <Link
              to="/forgot-password"
              className="text-sm font-semibold text-slate-500 hover:text-slate-900 transition-colors"
              data-testid="login-forgot-link"
            >
              Forgot password?
            </Link>
          </div>

          {/* Dev-only Quick Sign-In shortcuts.
              Belt-and-braces gating (ALLOWLIST — fail closed):
                1. `REACT_APP_SHOW_QUICK_SIGNIN === "true"` (build-time opt-in), AND
                2. Accounts supplied via REACT_APP_QUICK_SIGNIN_ACCOUNTS env, AND
                3. Hostname is explicitly a dev/preview host (localhost or the
                   Emergent preview domain). EVERY production host — including
                   `*.emergent.host` AND any custom domain (e.g. tow-hook.com) —
                   hides this block. Previously this was a blocklist that only
                   hid `*.emergent.host`, which leaked accounts on custom domains. */}
          {process.env.REACT_APP_SHOW_QUICK_SIGNIN === "true" &&
            quickRoles.length > 0 &&
            typeof window !== "undefined" &&
            (window.location.hostname === "localhost" ||
              window.location.hostname.endsWith(".preview.emergentagent.com")) && (
          <div className="mt-6 pt-6 border-t border-slate-200">
            <div className="uppercase-label mb-2">Quick Sign-In</div>
            <div className="space-y-2">
              {quickRoles.map((role) => (
                <button
                  key={role}
                  type="button"
                  onClick={() => quickFill(role)}
                  className="w-full text-left bg-white border border-slate-200 hover:border-blue-400 rounded-md px-3 py-2 text-sm transition-colors"
                  data-testid={`demo-${role}-btn`}
                >
                  <div className="font-semibold text-slate-900">{ROLE_META[role].label}</div>
                  <div className="text-xs text-slate-500">{QUICK_ACCOUNTS[role].email} · {ROLE_META[role].note}</div>
                </button>
              ))}
            </div>
          </div>
          )}
        </div>
      </div>
    </div>
  );
}
